import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook para verificar permisos del usuario logueado.
 *
 * Los permisos vienen del login response en user.permissions,
 * organizados por módulo:
 * {
 *   agents: { view_agents: { can_view, can_create, can_edit, can_delete }, ... },
 *   wall: { access_wall: { can_view: true, ... }, publish_wall: { can_view: true, ... } },
 *   ...
 * }
 *
 * Cada permiso se considera "habilitado" si can_view = true.
 *
 * Uso:
 *   const { can, canView, canAction, isSuperAdmin } = usePermissions();
 *   if (canView('agents')) { ... }          // ¿Puede ver el módulo?
 *   if (canAction('publish_wall')) { ... }  // ¿Tiene permiso específico?
 *   if (can('agents', 'edit')) { ... }      // Compatibilidad CRUD
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const helpers = useMemo(() => {
    const permissions = user?.permissions || {};
    const roleName = user?.role?.role_name || '';

    const isSuperAdmin = () => roleName === 'SuperAdmin';

    /**
     * Verifica si el usuario tiene permiso CRUD en un módulo (compatibilidad).
     */
    const can = (moduleName, action) => {
      if (isSuperAdmin()) return true;
      const mod = permissions[moduleName];
      if (!mod) return false;
      const flag = `can_${action}`;
      return Object.values(mod).some(p => p[flag] === true);
    };

    /**
     * Verifica si el usuario tiene un permiso específico habilitado.
     * El permiso se busca por permission_name en todos los módulos.
     * Se considera habilitado si can_view = true.
     * Ej: canAction('publish_wall'), canAction('send_message_chat')
     */
    const canAction = (permissionName) => {
      if (isSuperAdmin()) return true;
      for (const mod of Object.values(permissions)) {
        if (mod[permissionName] && mod[permissionName].can_view === true) {
          return true;
        }
      }
      return false;
    };

    const canView = (moduleName) => can(moduleName, 'view');
    const canCreate = (moduleName) => can(moduleName, 'create');
    const canEdit = (moduleName) => can(moduleName, 'edit');
    const canDelete = (moduleName) => can(moduleName, 'delete');

    const getUserCampaigns = () => user?.campaigns || [];
    const hasCampaignRestriction = () => (user?.campaigns || []).length > 0;

    return { can, canAction, canView, canCreate, canEdit, canDelete, isSuperAdmin, getUserCampaigns, hasCampaignRestriction };
  }, [user]);

  return helpers;
};

export default usePermissions;
